import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Card, Flex, Stack, useTheme, spacing } from '@fti/design-system';
import {
  useProject,
  useCreateProject,
  useUpdateProject,
  useRegisterProject,
  useProjectPublications,
  useSetProjectPublications,
  useProjectMembers,
  useAddMember,
  useRemoveMember,
} from '../../api/media-sim/projects';
import { WizardStepper } from '../../components/media-sim/projects/WizardStepper';
import { WizardStep1 } from '../../components/media-sim/projects/WizardStep1';
import { WizardStep2 } from '../../components/media-sim/projects/WizardStep2';
import { WizardStep3 } from '../../components/media-sim/projects/WizardStep3';
import { WizardStep4 } from '../../components/media-sim/projects/WizardStep4';
import { WizardStep5 } from '../../components/media-sim/projects/WizardStep5';
import { WizardStep6 } from '../../components/media-sim/projects/WizardStep6';
import type { Project, ProjectCreateRequest, ProjectUpdateRequest, ProjectMember } from '../../types/media-sim';

const STEP_LABELS = ['Identity', 'Date & Model', 'Publications', 'Topics', 'Subjects', 'Register'];
const BILLING_CODE_REGEX = /^\d{6}\.\d{4}-\d{3}$/;

function determineResumeStep(project: Project, pubCount: number): number {
  if (project.subject_terms.length > 0) return 6;
  if (project.topic_terms.length > 0) return 5;
  if (pubCount > 0) return 4;
  if (project.model_name && project.start_date) return 3;
  return 2;
}

export function ProjectWizardPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { theme } = useTheme();

  const [currentStep, setCurrentStep] = useState(1);
  const [draftId, setDraftId] = useState<string | null>(projectId || null);
  const [formData, setFormData] = useState<Partial<Project>>({});
  const [selectedPubIds, setSelectedPubIds] = useState<string[]>([]);
  const [members, setMembers] = useState<ProjectMember[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [registering, setRegistering] = useState(false);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);

  // Load existing project if resuming
  const { data: existingProject } = useProject(draftId || '');
  const { data: existingPubs, refetch: refetchPubs } = useProjectPublications(draftId || '');
  const { data: existingMembers } = useProjectMembers(draftId || '');

  const { createProject } = useCreateProject();
  const { updateProject } = useUpdateProject();
  const { registerProject } = useRegisterProject();
  const { setPublications } = useSetProjectPublications();
  const { addMember } = useAddMember();
  const { removeMember } = useRemoveMember();

  // Initialize from existing project when resuming
  useEffect(() => {
    if (existingProject && projectId) {
      setFormData(existingProject);
      if (existingPubs) {
        setSelectedPubIds(existingPubs.map((p) => p.id));
      }
      if (existingMembers) {
        setMembers(existingMembers);
      }
      const step = determineResumeStep(existingProject, existingPubs?.length || 0);
      setCurrentStep(step);
      const completed = Array.from({ length: step - 1 }, (_, i) => i + 1);
      setCompletedSteps(completed);
    }
  }, [existingProject, existingPubs, existingMembers, projectId]);

  const updateFormData = useCallback((updates: Partial<ProjectUpdateRequest>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  }, []);

  const handleNext = async () => {
    try {
      setErrors({});

      if (currentStep === 1) {
        if (!formData.project_name?.trim()) {
          setErrors({ project_name: 'Project name is required' });
          return;
        }
        if (!formData.client_name?.trim() || !formData.billing_code?.trim()) return;
        if (!BILLING_CODE_REGEX.test(formData.billing_code)) return;

        if (!draftId) {
          const created = await createProject({
            project_name: formData.project_name,
            client_name: formData.client_name,
            billing_code: formData.billing_code,
            model_name: formData.model_name,
            start_date: formData.start_date ?? undefined,
          } as ProjectCreateRequest);
          if (created) {
            setDraftId(created.id);
            if (formData.key_facts) {
              const updated = await updateProject(created.id, { key_facts: formData.key_facts });
              setFormData(updated ?? { ...created, key_facts: formData.key_facts });
            } else {
              setFormData(created);
            }
          }
        } else {
          await updateProject(draftId, {
            project_name: formData.project_name,
            client_name: formData.client_name,
            billing_code: formData.billing_code,
            key_facts: formData.key_facts,
          });
        }
      }

      if (currentStep === 2 && draftId) {
        await updateProject(draftId, {
          model_name: formData.model_name,
          start_date: formData.start_date ?? undefined,
        });
      }

      if (currentStep === 3 && draftId) {
        if (selectedPubIds.length === 0) return;
        await setPublications(draftId, selectedPubIds);
        refetchPubs();
      }

      if (currentStep === 4 && draftId) {
        if ((formData.topic_terms || []).length === 0) return;
        await updateProject(draftId, { topic_terms: formData.topic_terms });
      }

      if (currentStep === 5 && draftId) {
        if ((formData.subject_terms || []).length === 0) return;
        await updateProject(draftId, { subject_terms: formData.subject_terms });
      }

      setCompletedSteps((prev) =>
        prev.includes(currentStep) ? prev : [...prev, currentStep]
      );
      setCurrentStep((s) => Math.min(s + 1, 6));
    } catch (err: unknown) {
      const message = err && typeof err === 'object' && 'message' in err
        ? (err as { message: string }).message
        : 'Failed to save';
      setErrors({ _general: message });
    }
  };

  const handleBack = () => setCurrentStep((s) => Math.max(s - 1, 1));

  const handleRegister = async () => {
    if (!draftId) return;
    setRegistering(true);
    try {
      await registerProject(draftId);
      navigate(`/simulations/projects/${draftId}`);
    } catch {
      setErrors({ _general: 'Failed to register project' });
    } finally {
      setRegistering(false);
    }
  };

  const handleAddMember = async (userId: string) => {
    if (!draftId) return;
    try {
      const member = await addMember(draftId, userId);
      if (member) setMembers((prev) => [...prev, member]);
    } catch {
      // Ignore duplicate member errors
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    if (!draftId) return;
    try {
      await removeMember(draftId, memberId);
      setMembers((prev) => prev.filter((m) => m.id !== memberId));
    } catch {
      // Ignore errors
    }
  };

  return (
    <Stack gap="md">
      <h1 style={{ fontSize: '1.5rem', fontWeight: 600, color: theme.colors.text.primary, margin: 0 }}>
        {projectId ? 'Edit Project' : 'Create Project'}
      </h1>

      <WizardStepper
        currentStep={currentStep}
        totalSteps={6}
        stepLabels={STEP_LABELS}
        completedSteps={completedSteps}
      />

      {errors._general && (
        <p style={{ color: theme.colors.semantic.error.default, fontSize: '0.85rem', margin: 0 }}>
          {errors._general}
        </p>
      )}

      {currentStep === 1 && (
        <WizardStep1
          data={formData}
          onChange={updateFormData}
          members={members}
          onAddMember={handleAddMember}
          onRemoveMember={handleRemoveMember}
          errors={errors}
        />
      )}
      {currentStep === 2 && <WizardStep2 data={formData} onChange={updateFormData} />}
      {currentStep === 3 && (
        <WizardStep3 selectedIds={selectedPubIds} onChange={setSelectedPubIds} />
      )}
      {currentStep === 4 && (
        <WizardStep4
          topicTerms={formData.topic_terms || []}
          onChange={(terms) => updateFormData({ topic_terms: terms })}
          collectionIds={(existingPubs || []).filter((p) => selectedPubIds.includes(p.id)).map((p) => p.milvus_id)}
        />
      )}
      {currentStep === 5 && (
        <WizardStep5
          subjectTerms={formData.subject_terms || []}
          onChange={(terms) => updateFormData({ subject_terms: terms })}
          collectionIds={(existingPubs || []).filter((p) => selectedPubIds.includes(p.id)).map((p) => p.milvus_id)}
        />
      )}
      {currentStep === 6 && (
        <WizardStep6
          project={formData}
          publications={(existingPubs || []).filter((p) =>
            selectedPubIds.includes(p.id)
          )}
          members={members}
          onRegister={handleRegister}
          registering={registering}
        />
      )}

      {currentStep < 6 && (
        <Flex justify="between">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={currentStep === 1}
          >
            Back
          </Button>
          <Button variant="solid" color="primary" onClick={handleNext}>
            Next
          </Button>
        </Flex>
      )}
      {currentStep === 6 && (
        <Flex justify="start">
          <Button variant="outline" onClick={handleBack}>
            Back
          </Button>
        </Flex>
      )}
    </Stack>
  );
}
